import React from 'react';

interface PlayedCountFlagProps {
  count: number;
  /** Tailwind height class. The width follows from the viewBox, so only the height is set. */
  className?: string;
}

// BRAND.md: text on a gold fill is BLACK. Never white, never gold-on-gold, both are 2.32:1.
const GOLD = '#BBAA64';
const ON_GOLD = '#000000';

// The flag from the wordmark, traced off GolfPlayed.Web/brand/logos/golfplayed-wordmark-white.png
// rather than drawn by hand: the pennant this replaced was a lookalike, and the mark on the
// account menu has to be the mark. Sampled along the top and bottom edges of the gold at
// x 2771-3047, y 0-227 in that 3048x600 file, so the wave is the real one.
//
// No pole. In the wordmark the staff is the ascender of the "d" in "Played", so a copy of it
// standing alone next to a name is a stray gold bar, not the mark: it doubled the height of the
// drawing and read as a separate shape. The waving banner alone is what carries the count.
//
// Re-trace if the brand pack's wordmark ever changes.
const FLAG_PATH =
  'M0,33 L9,25 L17,16 L26,13 L35,9 L43,4 L52,4 L60,2 L69,2 L78,1 L86,0 L95,3 L104,5 L112,4 L121,9 L129,12 L138,16 L147,20 L155,23 L164,26 L173,29 L181,31 L190,32 L198,31 L207,31 L216,31 L224,30 L233,30 L242,27 L250,23 L259,16 L267,9 L276,0 L276,194 L267,205 L259,212 L250,216 L242,221 L233,221 L224,222 L216,223 L207,223 L198,223 L190,222 L181,221 L173,219 L164,218 L155,216 L147,214 L138,212 L129,210 L121,205 L112,205 L104,201 L95,199 L86,198 L78,199 L69,199 L60,198 L52,199 L43,201 L35,206 L26,210 L17,213 L9,219 L0,226 Z';

// The traced flag's own box. Everything else is measured in these units, so one height class
// scales the whole drawing.
const NATIVE_WIDTH = 276;
const NATIVE_HEIGHT = 227;

// The band the wave never eats into: the lowest the top edge ever sits, and the highest the bottom
// edge ever sits. The number lives between these two, so it cannot collide with a crest.
const SAFE_TOP = 33;
const SAFE_BOTTOM = 194;

const FONT_SIZE = 120;
// Rough advance widths for bold Montserrat at FONT_SIZE. Only used to size the flag, so a few
// units either way costs a little slack, not a clipped number.
const DIGIT_WIDTH = 76;
const SEPARATOR_WIDTH = 40;
const SIDE_PADDING = 45;

// The height of the whole mark. The drawing is now the banner and nothing else, so this is the
// banner's own height: 28px puts the digits at about the size of the name beside them, which is
// what the pole version reached at h-10 once its staff was taken out of the measurement.
const DEFAULT_HEIGHT = 'h-7';

const PlayedCountFlag: React.FC<PlayedCountFlagProps> = ({
  count,
  className = DEFAULT_HEIGHT,
}) => {
  // Below zero cannot happen, but a negative would silently produce a flag narrower than its text.
  const safeCount = Math.max(0, Math.floor(count));
  // Pinned to en-US rather than the browser locale: some locales group with a narrow no-break
  // space, which at this size reads as two separate numbers.
  const label = safeCount.toLocaleString('en-US');

  const textWidth = label
    .split('')
    .reduce(
      (total, character) =>
        total + (character === ',' ? SEPARATOR_WIDTH : DIGIT_WIDTH),
      0,
    );
  // Never narrower than the traced flag. Squeezing below its own width would flatten the wave into
  // something that is no longer the brand mark, and a one-digit count does not need the room.
  const flagWidth = Math.max(NATIVE_WIDTH, SIDE_PADDING * 2 + textWidth);
  const textCentre = (SAFE_TOP + SAFE_BOTTOM) / 2;

  return (
    <svg
      viewBox={`0 0 ${flagWidth} ${NATIVE_HEIGHT}`}
      // width:auto plus a height class: the viewBox aspect ratio does the widening, Tailwind only
      // sets the height.
      className={`w-auto shrink-0 ${className}`}
      role="img"
      aria-label={`${label} courses played`}
    >
      {/* Stretched on the x axis only. The flag is a banner, so a longer one is still the same
          flag; scaling both axes would shrink the number's room as it grew. */}
      <g transform={`scale(${flagWidth / NATIVE_WIDTH}, 1)`}>
        <path d={FLAG_PATH} fill={GOLD} />
      </g>
      <text
        x={flagWidth / 2}
        y={textCentre}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Montserrat, Segoe UI, system-ui, sans-serif"
        fontSize={FONT_SIZE}
        fontWeight="700"
        fill={ON_GOLD}
      >
        {label}
      </text>
    </svg>
  );
};

export default PlayedCountFlag;
